import React from "react";
import { KTMScreen, ActionEvent, RenderEvent, renderScreen } from "ktm-ui";
import { KTMApi } from "api/KTMApi";
import {
  Layout,
  Dialog,
  Button,
  Display,
  Line,
  Column,
  Panel,
  Card,
} from "ktm-ui";
import CustomToolTip from "../../components/CustomToolTip";
import axios from "axios";
import { Modal, Button as ReactButton } from "react-bootstrap";

interface IdsBenCat {
  Id: string;
  LSALabel: string;
  AllowLSADependentClaims: boolean;
  Policy_Id: string;
}

export default KTMScreen(
  { name: "enroll.ben-limit.edit-lsa-settings" },
  class extends React.Component<{ limit_id: string; employeesCount: number }> {
    data: {
      dsBenCat: IdsBenCat;
    };

    // Store original data for comparison
    originalData: IdsBenCat | null = null;

    state = {
      showCampaignWarning: false,
      expenseTypes: [],
      showConfirmation: false,
    };

    render() {
      return renderScreen(this);
    }

    async start({ screen }: ActionEvent) {
      screen.defaultRecord = "@dsBenCat";
      await KTMApi.start(screen, {
        "@limit_id": this.props.limit_id,
      });
      screen.title = "Edit LSA Options";
      await this.checkCampaignStatus();
      await this.getExpenseTypes();

      // Store a copy of the original data
      this.originalData = { ...this.data.dsBenCat };
    }

    main({ field, draw, action }: RenderEvent) {
      return (
        <Layout>
          {draw(this.header)}
          {this.state.showCampaignWarning && this.props.employeesCount > 0 && (
            <Card>
              <Line>
                <span
                  style={{ color: "red", fontSize: 14, fontWeight: "bold" }}
                >
                  This benefit category has been assigned to employees as part
                  of their year-end Flex Campaign. Any changes saved here will
                  apply to the next benefit year as well as to the current one,
                  and the assigned employees may be required to re-select.
                  <br />
                  If assistance is required, please click ‘Cancel’ and contact
                  enrollments@nationalhealthclaim.com.
                </span>
              </Line>
            </Card>
          )}
          {draw(this.main_tab)}
          {draw(this.footer)}
          {this.state.showConfirmation && this.renderPASAcknowledgment()}
        </Layout>
      );
    }

    header({ field, draw, action }: RenderEvent) {
      return <Dialog.Header label="Edit LSA Options" />;
    }

    footer({ field, draw, action }: RenderEvent) {
      return (
        <Dialog.Footer>
          <Button
            action={action(this.submit)}
            label="Save"
            size="large"
            style="primary"
          />
        </Dialog.Footer>
      );
    }

    main_tab({ field, draw, action }: RenderEvent) {
      return (
        <Panel>
          <Column>{draw(this.details)}</Column>
        </Panel>
      );
    }

    details({ field, draw, action }: RenderEvent) {
      return (
        <Card>
          <Display field={field("LSALabel")} />
          <Display
            field={field("AllowLSADependentClaims")}
            toolTip={
              <CustomToolTip
                header="Allow LSA Dependent Claims"
                bodyContent="This option determines if employees can reimburse LSA expenses for their dependents or if the LSA is an Employee Only benefit."
              />
            }
            onValueChanged={action(this.checkToggleAndPAS)}
          />
        </Card>
      );
    }

    renderPASAcknowledgment() {
      return (
        <Modal
          show={this.state.showConfirmation}
          style={{ top: 250, zIndex: 10000 }}
          centered
        >
          <Modal.Header>Acknowledgement</Modal.Header>
          <Modal.Body
            style={{ maxHeight: 400, overflowY: "auto", color: "red" }}
          >
            <Card>
              <Line>
                <span style={{ fontSize: 14 }}>
                  This category has the Pay-As-Submitted (PAS) expense type
                  assigned. <br />
                  If there are active dependents, NHC must confirm with the
                  client that in order to have PAS, LSA dependent claims must be
                  allowable. Please review the expense list before clicking
                  'Confirm'.
                </span>
              </Line>
            </Card>
          </Modal.Body>
          <Modal.Footer>
            <ReactButton
              variant="primary"
              onClick={() => {
                this.setState({ showConfirmation: false });
              }}
            >
              Confirm
            </ReactButton>
          </Modal.Footer>
        </Modal>
      );
    }

    async checkToggleAndPAS() {
      const isPASExisting = this.state.expenseTypes.some(
        (x) => x.shortName === "AA - PAY AS SUBMITTED",
      );

      // PAS is existing and un-toggling checkbox
      if (isPASExisting && !this.data.dsBenCat.AllowLSADependentClaims) {
        this.setState({ showConfirmation: true });
      }
    }

    getChangedFields(originalData, updatedData) {
      if (!originalData) return {};

      const changes = {};
      for (const key in updatedData) {
        if (updatedData[key] !== originalData[key]) {
          changes[key] = {
            oldValue:
              originalData[key] !== undefined ? originalData[key] : "N/A",
            newValue: updatedData[key] !== undefined ? updatedData[key] : "N/A",
          };
        }
      }
      return changes;
    }

    async submit({ screen }: ActionEvent) {
      // Track changes
      const changes = this.getChangedFields(
        this.originalData,
        this.data.dsBenCat,
      );

      const logDetails = Object.entries(changes)
        .map(([key, value]) => {
          const change = value as { oldValue: any; newValue: any };
          return `${key}: ${change.oldValue} → ${change.newValue}`;
        })
        .join(" | ");

      const policyLogs = {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          Note: logDetails,
          Action: "Policy Update LSA options",
          PolicyId: this.data.dsBenCat.Policy_Id,
        }),
      };

      await KTMApi.action(screen, "submit", {
        "@dsBenCat.LSALabel": this.data.dsBenCat.LSALabel,
        "@dsBenCat.AllowLSADependentClaims":
          this.data.dsBenCat.AllowLSADependentClaims,
        "@limit_id": this.props.limit_id,
      }).then(() => {
        fetch("/api/PlanAdmin/updatepolicylogs", policyLogs);
      });
      screen.close({ continue: true });
    }

    async getExpenseTypes() {
      axios
        .get(
          `/api/BenefitCategories/getexpensetypes?policyId=${this.data.dsBenCat.Policy_Id}&limit_id=${this.props.limit_id}`,
        )
        .then((res) => {
          if (res.data.success) {
            this.setState({
              expenseTypes: res.data.expenseTypes,
            });
          } else {
            console.error(res.data.message, "Error");
          }
        })
        .catch((error) => {
          console.error(error, "Error");
        });
    }

    async checkCampaignStatus() {
      let response = await fetch(
        "/api/Utility/checkcampaignstatus?policyId=" +
          this.data.dsBenCat.Policy_Id,
      );
      let res = await response.json();
      if (res.isCampaignOpen) {
        this.setState({ showCampaignWarning: true });
      }
    }
  },
);
